import { Alert, Box, Button, LinearProgress, Paper, Stack, Typography } from "@mui/material";
import { useState } from "react";
import { useMountDate } from "../../../utils/hooks/use-mount-date";
import { ChannelIcon } from "../../channels/components/channel-icon";
import { useProgramTimeline } from "../hooks/use-program-timeline";
import { communicationsText } from "../program-timeline";
import { ALL_BUT_ARCHIVED, DEFAULT_STATUSES, filterTimeline } from "../timeline-filter";
import { CommunicationActions } from "./communication-actions";
import { GroupTimeline } from "./group-timeline";
import { LayoutToggle } from "./layout-toggle";
import { TimelineFilters } from "./timeline-filters";
import { CHANNEL_LABELS, isChannel } from "./timeline-parts";
import { TimelineTable } from "./timeline-table";

import type { ChannelName } from "../../channels/types";
import type { ProgramNode, ProgramTree } from "../../program-tree/program-tree";
import type { TimelineRow } from "../program-timeline";
import type { TimelineFilter } from "../timeline-filter";
import type { TimelineLayout } from "./layout-toggle";
import type { FC, ReactNode } from "react";

/** The program and each program below it. */
const subtreeIds = (node: ProgramNode): ProgramNode["id"][] => [
  node.id,
  ...node.children.flatMap(subtreeIds),
];

/**
 * The Communications of the program or folder that the tabs show, and of the folders below it, on
 * a timeline for each event that they send from, or in a list, with the channels that they use.
 * Chips for each event and each status turn them on and off, and only active and draft ones are
 * on at the start. A Communication in a folder below says the folder's name. A Communication in a
 * program that the caller can edit can be copied, moved or archived.
 */
export const ProgramTimeline: FC<{
  tree: ProgramTree;
  focus: ProgramNode;
  /** After a Communication is moved or archived, so that the counts can load again. */
  onChanged?: () => void;
}> = ({ tree, focus, onChanged }) => {
  const today = useMountDate();
  const [layout, setLayout] = useState<TimelineLayout>("timeline");
  const [filter, setFilter] = useState<TimelineFilter>({
    statuses: DEFAULT_STATUSES,
    hiddenEvents: [],
  });
  const { data, error, reload } = useProgramTimeline(
    subtreeIds(focus),
    filter.statuses.includes("archived"),
  );

  if (error) return <Alert severity="error">The communications could not be loaded.</Alert>;
  if (!data) return <LinearProgress />;

  const { rows, archived } = data;
  const any = rows.length > 0 || archived > 0;
  const view = filterTimeline(rows, filter, today, archived);
  const folderOf = ({ program_id }: TimelineRow): string | null =>
    program_id && program_id !== focus.id ? (tree.byId.get(program_id)?.title ?? null) : null;
  const changed = (): void => {
    reload();
    onChanged?.();
  };
  const actionsOf = (row: TimelineRow): ReactNode => (
    <CommunicationActions row={row} tree={tree} onChanged={changed} />
  );
  const channels = Object.keys(CHANNEL_LABELS).filter(
    (channel): channel is NonNullable<ChannelName> =>
      isChannel(channel) && view.rows.some((row) => row.channel === channel),
  );

  return (
    <Paper
      component="section"
      aria-label="Communications by event"
      sx={{ borderRadius: "4px", boxShadow: "0 1px 6px rgba(100, 116, 139, 0.12)" }}
    >
      <Stack spacing={1.5} sx={{ px: 2, pt: 2, pb: 1.75, borderBottom: 1, borderColor: "divider" }}>
        <Stack direction="row" useFlexGap sx={{ alignItems: "center", flexWrap: "wrap", gap: 1.5 }}>
          <Stack spacing={0.25} sx={{ minWidth: 0 }}>
            <Typography component="h2" sx={{ fontSize: 16, fontWeight: 600, lineHeight: "24px" }}>
              Communications
            </Typography>
            <Stack
              direction="row"
              useFlexGap
              sx={{
                alignItems: "center",
                flexWrap: "wrap",
                columnGap: 1.25,
                fontSize: 13,
                lineHeight: "20px",
                color: "text.secondary",
              }}
            >
              <span>
                {communicationsText(view.rows.length)}
                {channels.length ? " ·" : null}
              </span>
              {channels.map((channel) => (
                <Box
                  key={channel}
                  component="span"
                  sx={{ display: "inline-flex", alignItems: "center", gap: 0.625 }}
                >
                  <ChannelIcon channel={channel} sx={{ fontSize: 16 }} />
                  {CHANNEL_LABELS[channel]}
                </Box>
              ))}
            </Stack>
          </Stack>
          <Box sx={{ flex: 1 }} />
          {any ? <LayoutToggle value={layout} onChange={setLayout} /> : null}
        </Stack>
        {any ? <TimelineFilters view={view} filter={filter} onChange={setFilter} /> : null}
      </Stack>
      {!any ? (
        <Typography variant="body2" sx={{ p: 2, color: "text.secondary" }}>
          No communications yet.
        </Typography>
      ) : !view.rows.length ? (
        <Stack spacing={1} sx={{ p: 3, alignItems: "center" }}>
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            No communications match these filters.
          </Typography>
          <Button
            onClick={() => setFilter({ statuses: ALL_BUT_ARCHIVED, hiddenEvents: [] })}
            data-analytics-id="program-timeline-show-all"
          >
            Show all statuses
          </Button>
        </Stack>
      ) : layout === "list" ? (
        <TimelineTable groups={view.groups} folderOf={folderOf} actionsOf={actionsOf} />
      ) : (
        <Stack spacing={2.5} sx={{ px: 2, pt: 0.5, pb: 3 }}>
          {view.groups.map((group) => (
            <GroupTimeline
              key={group.key}
              group={group}
              folderOf={folderOf}
              actionsOf={actionsOf}
            />
          ))}
        </Stack>
      )}
    </Paper>
  );
};
